// @ts-nocheck
import React, { useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MENU } from "../data";
import { useLanguageContext } from "../context/languageContext";
import MenuImage from "./MenuImage";
import { CategoryTabs } from "./CategoryTabs";
import useActiveSection from "../hooks/useActiveSection";

/**
 * The whole menu on one page: a section per category, each one a horizontal
 * snap-scrolling row of dishes. A guest scanning the QR code sees actual food
 * straight away and swipes through it, instead of tapping into a category first.
 */

const nameOf = (product, language) => {
    if (!product?.itemTranslation) return product.name;
    const map = {
        en: 'englishName', it: 'italianName', sq: 'albanianName', de: 'germanName',
        es: 'spanishName', pl: 'polishName', ukr: 'ukrainianName',
    };
    return product.itemTranslation[map[language]] || product.name;
};

const MenuRowCard = ({ product, categoryId }) => {
    const navigate = useNavigate();
    const { language } = useLanguageContext();

    const name = nameOf(product, language);

    // Tapping a dish opens the full menu at that dish.
    const openInMenu = () => {
        const id = categoryId ?? product.menuCategoryId;
        navigate(`/menu?categoryId=${id}&itemId=${product.id}`);
    };

    return (
        <button
            onClick={openInMenu}
            className="group snap-start flex-shrink-0 w-[152px] sm:w-[168px] text-left bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-[0_2px_10px_-6px_rgba(0,0,0,0.15)] active:scale-[0.97] transition-transform"
        >
            <div className="relative w-full aspect-[4/3] bg-slate-50 overflow-hidden">
                <MenuImage
                    src={product.imageUrl}
                    alt={name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
            </div>

            <div className="p-2.5 space-y-1.5">
                <h3 className="text-[12.5px] font-bold text-slate-900 leading-snug line-clamp-2 min-h-[2.2em]">
                    {name}
                </h3>
                <div className="flex items-baseline gap-1.5 flex-wrap">
                    <span className="text-sm font-black text-brand tracking-tight">
                        {product.price}
                    </span>
                    <span className="text-[9px] font-bold uppercase text-slate-400">
                        ALL
                    </span>
                </div>
            </div>
        </button>
    );
};

const MenuRow = ({ category }) => {
    const { t } = useLanguageContext();
    const items = category.menuItems || [];

    if (items.length === 0) return null;

    return (
        // scroll-mt clears the fixed header and the category bar when jumped to
        <section
            data-section-id={category.id}
            style={{ scrollMarginTop: "calc(var(--app-header-h, 64px) + var(--app-tabbar-h, 54px) + 12px)" }}
            className="mb-7"
        >
            <div className="px-5 mb-3 flex items-end justify-between gap-3">
                <div className="space-y-1.5 min-w-0">
                    <h2 className="text-lg font-clash text-slate-950 leading-none truncate">
                        {t(`categories.${category.categoryName}`, category.categoryName)}
                    </h2>
                    <div className="h-1 w-8 bg-brand rounded-full"></div>
                </div>
                <Link
                    to={`/menu?categoryId=${category.id}`}
                    className="flex-shrink-0 text-[10px] font-black uppercase tracking-widest text-brand"
                >
                    {t("viewAll", "View all")} · {items.length}
                </Link>
            </div>

            <div className="overflow-x-auto hide-scrollbar snap-x snap-mandatory overscroll-x-contain">
                <div className="flex gap-3 px-5 pb-1">
                    {items.map((item, index) => (
                        <MenuRowCard
                            key={item.id || index}
                            product={item}
                            categoryId={category.id}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

export const MenuSections = () => {
    const { t } = useLanguageContext();
    const categories = MENU;

    const activeId = useActiveSection(140, (categories || []).length > 0);

    const jumpTo = useCallback((id) => {
        const el = document.querySelector(`[data-section-id="${id}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    if (!categories || categories.length === 0) {
        return (
            <div className="py-20 text-center">
                <p className="text-slate-400 text-sm font-medium">{t("Coming Soon")}</p>
            </div>
        );
    }

    return (
        <div className="pt-2 pb-2">
            <div className="px-5 mb-5">
                <h2 className="text-xl font-clash text-slate-950">{t("The Menu")}</h2>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mt-1">
                    {categories.reduce((n, c) => n + (c.menuItems?.length || 0), 0)} {t("dishes", "dishes")}
                </p>
            </div>

            {/* Category bar: sticks under the header once the welcome banner scrolls away.
                No wrapper element - a sticky child can only stick within its parent's
                box, and a wrapper sized to the bar makes it scroll straight off. */}
            <CategoryTabs
                categories={categories}
                activeId={activeId}
                onSelect={(category) => jumpTo(category.id)}
                className="mb-4"
            />

            {categories.map((category, index) => (
                <MenuRow key={category.id || index} category={category} />
            ))}
        </div>
    );
};

export default MenuSections;
